import styled from '@emotion/styled';
import { css, cx } from '@emotion/css';
import { Row, Col, Space, Button, message, Spin, Tag, Tooltip } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useToggle } from 'ahooks';
import { noop } from 'lodash-es';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { Fragment, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { formatNumber } from 'utils/numberFormat';
import TradeDetailIncomeShopee from 'views/Conversation/Sider/TradeDetailIncomeShopee';
import { updateTradeComment } from 'services/api/conversation';
import { SimpleCard } from 'views/Conversation/Sider/TradeProduct';
import CopyOrigin from 'views/Conversation/Sider/Copy';
import TradeCommentForm from 'views/Conversation/Sider/TradeCommentForm';
import LogisticsList from 'views/Conversation/Sider/LogisticsList';
import { TradeContext } from 'views/Conversation/Sider/StateContext';
import { useGetReasonLabel } from 'hooks/business/refundReason/useGetReasonLabel';
import { toUpperCase } from 'utils/toLowerCase';
import { ReturnOrderListItemV2 } from 'services/api/refund';
import OperationLogs from 'components/Refund/OperationLogs';
import TranslateToggler from '../../../../components/TranslateToggler';
import { ReturnRecord } from '../ReturnRecord';
import { isTiKTok } from 'lib/utils/isTikTok';
import SiderImageSlide from 'views/Conversation/Sider/SiderImageSlide';
import { PreviewModalProvider } from 'views/Conversation/Sider/PreviewModal';
import { ProductTagList, TagProduct } from 'beisi/TagProduct';
import TradeDetailIncomeLazada from 'views/Conversation/Sider/TradeDetailIncomeLazada';
import TradePaymentAmountLazada from 'views/Conversation/Sider/TradeDetailPaymentLazada';
import TradePaymentAmountShopee from 'views/Conversation/Sider/TradeDetailPaymentShopee';
import { TradeDetailPaymentTikTok } from 'views/Conversation/Sider/TradeDetailPaymentTikTok';
import copyText from 'utils/copyText';
import MercadoLogistics from 'views/Conversation/Sider/MercadoLogistics';
import { useGetMercadoReasonLabel } from 'hooks/business/refundReason/useGetMercadoReasonLabel';
import { BookingTag } from 'views/Conversation/Sider/TradeComponentDetail';
import TradeDetailIncomeMercado from 'views/Conversation/Sider/TradeDetailIncomeMercado';
import { useTranslateReport } from 'hooks/useTranslateReport';
import { getAppProductName } from 'hooks/useTrans';
import { useRefundDrawerBody } from './hooks/useRefundDrawerBody';
import type { RefundDrawerDataSource, RefundDrawerRow } from './types';
import { dateFormatter } from '../ReturnListItem/Logistics';
import { LogisticsContent } from 'views/Conversation/Sider/TradeDetailLogistics';

const supportOfficialPlatforms: General.PlatformUpperCaseTunnel[] = [
  'LAZADA',
  'TIKTOK',
  'TIKTOK_US',
];

const Copy = styled(CopyOrigin)`
  font-size: 0;
  vertical-align: text-bottom;
`;
const ReasonTag = styled.div`
  display: inline-block;
  color: var(--fett-scenario-accent-text);
  background-color: var(--fett-bg-panel);
  border: 1px solid #d1dfff;
  padding: 0 8px;
  border-radius: 2px;
  margin: 10px 0;
`;
const ReasonContent = styled.div`
  margin: 8px 16px;
  line-height: 1.2em;
`;

const List = styled.div`
  border: 1px solid var(--fett-border-default);
  margin: 18px 0;
  padding: 0 0 10px 0;
`;
const ListTitle = styled(Row)`
  background: var(--fett-bg-info-tip);
  height: 40px;
  padding: 0 12px;
`;
const ListBody = styled.div`
  padding: 0 12px;
`;
const Content = styled.div`
  border-bottom: 1px solid var(--fett-border-default);
  padding: 12px 0 16px 0;
  margin: 10px 0;
  &::last-of-type {
    border-bottom: 0;
  }
  h3 {
    position: relative;
    font-size: 13px;
    padding: 0 18px;
    font-weight: 600;
    &::before {
      content: '';
      position: absolute;
      height: 100%;
      top: 50%;
      left: 0;
      border-left: 4px solid #3370ff;
      transform: translateY(-50%);
    }
  }
  section {
    padding: 0 16px;
  }
`;

const ItemCard = styled(SimpleCard)`
  margin: 10px -5px;
  section & {
    padding: 15px;
  }
`;

const StatusUI = styled.div`
  margin: 16px;
  padding: 12px 16px;
  border: 1px solid var(--fett-border-default);
  h3 {
    color: var(--fett-text-primary);
    font-size: 16px;
  }
`;

const PreOrderTag = styled(Tag)`
  border: none;
  color: var(--fett-scenario-success-text);
  padding: 0 4px;
  font-size: 12px;
  background: var(--fett-surface-success-bg);
`;

/** Body 内订单备注编辑/展示一行 */
const CommentLine = ({
  platform,
  trade,
  onUpdate,
}: {
  platform: General.PlatformUpperCase;
  trade: Conversation.Trade.Vo;
  onUpdate: (comment: Conversation.Trade.Vo['sellerMemos']) => void;
}) => {
  const { t } = useTranslate();
  const [editing, { setRight: setEditing, setLeft: setNotEditing }] = useToggle(false);
  const sellerMemos =
    platform === 'SHOPEE' && trade.editSellerMemoType !== 'LECHAT'
      ? trade.sellerMemos
      : trade.lechatSellerMemos;
  const commentComp = useMemo(() => {
    return editing ? (
      <TradeCommentForm
        initialValues={{
          platform,
          sellerMemos,
          internalTradeId: trade.internalTradeId,
          editSellerMemoType: trade.editSellerMemoType,
        }}
        onSubmit={(values) => {
          return updateTradeComment(values)
            .then(() => {
              const { sellerMemos } = values;
              message.success(
                t('scene.orderWorkspace.returnRefundTab.detail.feedback.updateCommentSuccess'),
              );
              batchedUpdates(() => {
                onUpdate(sellerMemos);
                setNotEditing();
              });
            })
            .catch(() => {
              message.error(
                t('scene.orderWorkspace.returnRefundTab.detail.feedback.updateCommentFailed'),
              );
              setEditing();
            });
        }}
      >
        {(form, props) => {
          const { isSubmitting, handleSubmit } = props;
          return (
            <div
              className={cx(
                css`
                  padding-top: 10px;
                `,
              )}
            >
              <Spin spinning={isSubmitting}>
                {form}
                <Row justify="end" gutter={10}>
                  <Col>
                    <Button size="small" onClick={setNotEditing}>
                      {t('scene.orderWorkspace.returnRefundTab.action.cancel')}
                    </Button>
                  </Col>
                  <Col>
                    <Button size="small" type="primary" onClick={() => handleSubmit()}>
                      {t('common.action.save')}
                    </Button>
                  </Col>
                </Row>
              </Spin>
            </div>
          );
        }}
      </TradeCommentForm>
    ) : (
      <div>{sellerMemos}</div>
    );
  }, [
    platform,
    editing,
    onUpdate,
    setEditing,
    setNotEditing,
    trade.internalTradeId,
    trade.editSellerMemoType,
    sellerMemos,
    t,
  ]);
  return (
    <>
      <div
        className={cx(css`
          background-color: var(--fett-erp-card-bg);
          color: var(--fett-text-secondary);
          padding: 10px;
          margin: 14px 0;
          font-size: 14px;
        `)}
      >
        <Row align="middle" justify="space-between">
          <Col>
            {t('scene.orderWorkspace.returnRefundTab.detail.field.commentContent')}
            {supportOfficialPlatforms.includes(platform) || trade.editSellerMemoType === 'LECHAT'
              ? `(${getAppProductName()})`
              : ''}
          </Col>
          {editing ? null : (
            <Col onClick={setEditing}>
              <Button type="link">
                {sellerMemos
                  ? t('scene.orderWorkspace.returnRefundTab.detail.action.updateComment')
                  : t('scene.orderWorkspace.returnRefundTab.detail.action.createComment')}
              </Button>
            </Col>
          )}
        </Row>
        {commentComp}
      </div>
      {platform === 'SHOPEE' && trade.editSellerMemoType === 'LECHAT' && trade.sellerMemos && (
        <div
          className={cx(css`
            background-color: var(--fett-erp-card-bg);
            color: var(--fett-text-secondary);
            padding: 10px;
            margin: 14px 0;
            font-size: 14px;
          `)}
        >
          <Row align="middle" justify="space-between">
            <Col>{t('scene.orderWorkspace.returnRefundTab.detail.field.commentContent')}</Col>
            {editing ? null : (
              <Col onClick={setEditing}>
                <Tooltip
                  title={t('scene.orderWorkspace.returnRefundTab.hint.shopeeOrderInfoNotAvailable')}
                >
                  <Button type="link">
                    {t('scene.orderWorkspace.returnRefundTab.detail.action.updateComment')}
                  </Button>
                </Tooltip>
              </Col>
            )}
          </Row>
          <div>{trade.sellerMemos}</div>
        </div>
      )}
      {supportOfficialPlatforms.includes(platform as General.PlatformUpperCaseTunnel) &&
        trade.sellerMemos && (
          <div
            className={cx(css`
              background-color: var(--fett-erp-card-bg);
              color: var(--fett-text-secondary);
              padding: 10px;
              margin: 14px 0;
              font-size: 14px;
            `)}
          >
            {trade.sellerMemos}
          </div>
        )}
    </>
  );
};

export interface RefundDrawerBodyProps {
  trade: Conversation.Trade.Vo;
  platform: General.Platform;
  dataSource: RefundDrawerDataSource;
  row: RefundDrawerRow;
  returnSn: string;
  refundAmount: number;
  isRefund: boolean;
  statusTip: { date: string; money: string; platform: string };
  conversation?: Conversation.Conversation;
  pullTrade: (refresh?: boolean) => void;
  displaySensitive: boolean;
}

/** 退款/取消抽屉 Body：原因、状态、物流、商品列表、备注、操作记录 */
export function RefundDrawerBody({
  trade,
  platform,
  dataSource,
  row,
  returnSn,
  refundAmount,
  isRefund,
  statusTip,
  conversation,
  pullTrade,
  displaySensitive,
}: RefundDrawerBodyProps) {
  const { t } = useTranslate();
  const { getReasonLabel } = useGetReasonLabel();
  const getMercadoReasonLabel = useGetMercadoReasonLabel();
  const translateReport = useTranslateReport();

  const { refundItems, reasonRaw, displayTradeId, tradeItems, statusContent } = useRefundDrawerBody(
    {
      trade,
      platform,
      returnSn,
      row,
      statusTip,
      isRefund,
    },
  );

  const returnRow = row as ReturnOrderListItemV2;
  const {
    unit,
    operationLog = [],
    containsPreOrderItem,
    returnTrackingNumber,
    returnShipmentId,
  } = returnRow;
  const { matchBooking, bookingId, internalTradeId, region, sellerOrderUrl } = trade;
  const logisticsList = (trade as any).logisticsList;
  const lazadaLogistics = (trade as any).lazadaLogistics;
  const mercadoLogistics = (trade as any).mercadoLogistics;
  const reverseLogisticsList = (trade as any).reverseLogisticsList;
  const internalStoreId = returnRow.internalStoreId ?? dataSource.internalStoreId;

  const currentReverseLogisticsList = reverseLogisticsList?.filter(
    (_: any) => _.externalReturnId === returnSn,
  );

  const reasonTransComp = dataSource.textReason ? (
    <ReasonContent>
      <TranslateToggler
        showTrans
        text={dataSource.textReason}
        defaultMap={dataSource.textReasonTrans as Record<string, string>}
        reporter={
          isRefund
            ? (_t, _l, map) => {
                translateReport({ returnSn, translation: map }, 'ReturnReason');
              }
            : undefined
        }
      />
    </ReasonContent>
  ) : null;

  let reasonContent: React.ReactNode = null;
  if (!isRefund) {
    reasonContent = (
      <Content>
        <h3>{t('scene.orderWorkspace.returnRefundTab.list.column.cancelReason')}</h3>
        {dataSource.textReason ? (
          <ReasonContent>
            {getReasonLabel(dataSource.textReason, toUpperCase(platform))}
          </ReasonContent>
        ) : null}
      </Content>
    );
  } else {
    reasonContent = [
      <Content key="reason">
        <h3>{t('scene.orderWorkspace.returnRefundTab.detail.field.returnRefundReason')}</h3>
        <section>
          {reasonRaw ? (
            <>
              <div>
                <ReasonTag>
                  {platform === 'mercado'
                    ? getMercadoReasonLabel(reasonRaw.reasonName)
                    : getReasonLabel(reasonRaw.reason, toUpperCase(platform))}
                </ReasonTag>
                {(() => {
                  const { image = [], returnVideo = [] } = reasonRaw;
                  const video = returnVideo.map((r: { videoUrl: string }) => r.videoUrl);
                  const length = image.length + video.length;
                  return (
                    <PreviewModalProvider>
                      <SiderImageSlide
                        videos={video}
                        images={image}
                        count={length}
                        style={{ width: `${85 * length - 5}px` }}
                        index={0}
                        width={640}
                        offsetPercent={2 / length}
                        extra={{
                          platform: toUpperCase(platform),
                          region,
                          storeId: internalStoreId,
                          tradeId: internalTradeId,
                        }}
                      />
                    </PreviewModalProvider>
                  );
                })()}
                {reasonTransComp}
              </div>
            </>
          ) : null}
        </section>
      </Content>,
      platform === 'shopee' &&
      reasonRaw?.reassessedRequestReason &&
      reasonRaw.reassessedRequestReason !== 'NONE' ? (
        <Content key="reassessed">
          <h3>{t('scene.orderWorkspace.returnRefundTab.list.column.reviewedReturnReason')}</h3>
          <section>
            <ReasonTag>
              {getReasonLabel(reasonRaw.reassessedRequestReason, toUpperCase(platform))}
            </ReasonTag>
          </section>
        </Content>
      ) : null,
    ];
  }

  return (
    <>
      {statusContent && (
        <StatusUI>
          <h3>{statusContent.title}</h3>
          {statusContent.description ? <p>{statusContent.description}</p> : null}
        </StatusUI>
      )}
      {reasonContent}
      {isTiKTok(platform) && reasonRaw && (
        <ReturnRecord type="panel" data={reasonRaw.returnHistory} returnSn={reasonRaw.returnSn} />
      )}
      <Content>
        <TradeContext.Provider value={{ state: trade, setState: noop }}>
          {platform === 'shopee' && logisticsList && (
            <>
              <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.logisticsInfo')}</h3>
              <section>
                <LogisticsList
                  sendable={false}
                  platform="SHOPEE"
                  logisticsPackageList={logisticsList}
                />
              </section>
            </>
          )}
          {(platform === 'shopee' || platform === 'mercado') &&
            currentReverseLogisticsList?.length && (
              <>
                <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.returnLogistics')}</h3>
                <div className="flex mb-2 ml-4">
                  <div className="mr-4 text-[var(--fett-text-secondary)]">
                    <div>
                      {t('scene.orderWorkspace.returnRefundTab.detail.field.logisticsStatus')}
                    </div>
                    {currentReverseLogisticsList[0].leChatStatus === 'LE_CHAT_SIGNED' ||
                    (Number(currentReverseLogisticsList[0].deliverySlaTimeMinMs) &&
                      Number(currentReverseLogisticsList[0].deliverySlaTimeMaxMs)) ? (
                      <div>
                        {t(
                          currentReverseLogisticsList[0].leChatStatus === 'LE_CHAT_SIGNED'
                            ? 'scene.orderWorkspace.returnRefundTab.detail.label.deliveryDate'
                            : 'scene.orderWorkspace.returnRefundTab.detail.label.estimatedDeliveryDate',
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div>
                      {t(`leChatLogisticsStatus.${currentReverseLogisticsList[0].leChatStatus}`)}
                    </div>
                    {currentReverseLogisticsList[0].leChatStatus === 'LE_CHAT_SIGNED' ? (
                      <div>
                        {t('scene.orderWorkspace.returnRefundTab.detail.field.deliveredOnDate', {
                          date: dateFormatter(currentReverseLogisticsList[0].deliveryTimeMs),
                        })}
                      </div>
                    ) : Number(currentReverseLogisticsList[0].deliverySlaTimeMinMs) &&
                      Number(currentReverseLogisticsList[0].deliverySlaTimeMaxMs) ? (
                      <div>
                        {t(
                          'scene.orderWorkspace.returnRefundTab.detail.field.estimatedDeliveryDateRange',
                          {
                            date1: dateFormatter(
                              currentReverseLogisticsList[0].deliverySlaTimeMinMs,
                            ),
                            date2: dateFormatter(
                              currentReverseLogisticsList[0].deliverySlaTimeMaxMs,
                            ),
                          },
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
                <section className="mb-4">
                  <LogisticsContent
                    isReverse
                    externalReturnId={returnSn}
                    sendable={false}
                    showCopyAll={false}
                    reportTranslation={false}
                    platform="SHOPEE"
                    logisticsPackageList={reverseLogisticsList || []}
                    fold
                    foldNum={2}
                  />
                </section>
              </>
            )}
          {platform === 'lazada' && lazadaLogistics?.packages && (
            <>
              <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.logisticsInfo')}</h3>
              <section>
                <LogisticsList
                  sendable={false}
                  platform="LAZADA"
                  logisticsPackageList={lazadaLogistics.packages}
                />
              </section>
            </>
          )}
          {platform === 'mercado' && mercadoLogistics && (
            <>
              <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.logisticsInfo')}</h3>
              <section>
                <MercadoLogistics
                  isDetail
                  logistics={mercadoLogistics}
                  sellerOrderUrl={sellerOrderUrl}
                />
              </section>
            </>
          )}
        </TradeContext.Provider>
        <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.requestedProducts')}</h3>
        <section>
          {isRefund && (
            <List>
              <ListTitle align="middle" justify="space-between">
                <Col>{t('scene.orderWorkspace.returnRefundTab.detail.section.returnRefund')}</Col>
                <Col className="flex-1">
                  <div className="flex justify-end items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      <span>
                        {t('scene.orderWorkspace.returnRefundTab.detail.field.refundId')}:{returnSn}
                      </span>
                      <Copy
                        text={returnSn}
                        successMessage={t('common.feedback.copySuccess')}
                        failedMessage={t('common.feedback.copyFailed')}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {returnTrackingNumber && platform !== 'shopee' && (
                      <div className="flex items-center gap-0.5">
                        <span>
                          {t('scene.orderWorkspace.returnRefundTab.detail.field.trackingNumber')}:{' '}
                          {returnTrackingNumber}
                        </span>
                        <CopyOutlined
                          onClick={() =>
                            copyText(returnTrackingNumber).then(() =>
                              message.success(t('common.feedback.copySuccess')),
                            )
                          }
                        />
                      </div>
                    )}
                    {returnShipmentId && platform === 'mercado' && (
                      <div className="flex items-center gap-0.5">
                        <span>
                          {t('物流面单号')}: {returnShipmentId}
                        </span>
                        <CopyOutlined
                          onClick={() =>
                            copyText(returnShipmentId).then(() =>
                              message.success(t('common.feedback.copySuccess')),
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                </Col>
              </ListTitle>
              <ListBody>
                {refundItems.map((it) => (
                  <ItemCard
                    platform={platform}
                    region={trade.region}
                    unit={trade.unit}
                    key={it.itemId}
                    product={it}
                    externalShopId={trade.externalShopId}
                    showReturn
                  />
                ))}
                <Row justify="end">
                  <Col>
                    {t('scene.orderWorkspace.returnRefundTab.detail.field.totalRefundAmount')}:
                    {trade.unit}
                    {formatNumber(refundAmount, '0,0.00')}
                  </Col>
                </Row>
              </ListBody>
            </List>
          )}
          <List>
            <ListTitle align="middle" justify="space-between">
              <Col>
                {t('scene.orderWorkspace.returnRefundTab.detail.section.originalOrderInfo')}
                {containsPreOrderItem ? (
                  <PreOrderTag color="green" title={t('domain.product.type.preOrder')}>
                    {t('domain.product.type.preOrder')}
                  </PreOrderTag>
                ) : null}
              </Col>
              <Col>
                {matchBooking && (
                  <Space className="mr-[8px]">
                    <BookingTag style={{ color: '#F4B432', background: '#FAEDD2' }}>
                      {t('scene.orderWorkspace.returnRefundTab.detail.action.matchOrder')}
                    </BookingTag>
                    {bookingId
                      ? [
                          <span key="label">BookingID:{bookingId}</span>,
                          <Copy
                            key="copy"
                            text={bookingId}
                            successMessage={t('common.feedback.copySuccess')}
                            failedMessage={t('common.feedback.copyFailed')}
                          />,
                        ]
                      : null}
                  </Space>
                )}
                <Space>
                  <span>
                    {t('scene.orderWorkspace.returnRefundTab.detail.field.orderId')}:
                    {displayTradeId}
                  </span>
                  <Copy
                    text={displayTradeId}
                    successMessage={t('common.feedback.copySuccess')}
                    failedMessage={t('common.feedback.copyFailed')}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Space>
              </Col>
            </ListTitle>
            <ListBody>
              {tradeItems.map((it) => (
                <Fragment key={it.itemId}>
                  <ItemCard
                    platform={platform}
                    region={trade.region}
                    unit={trade.unit}
                    product={it}
                    externalShopId={trade.externalShopId}
                    showReturn
                  />
                  <ProductTagList productData={it} trade={trade} />
                </Fragment>
              ))}
              <TagProduct
                productData={tradeItems}
                border={['top', 'bottom']}
                className="py-[8px]"
                trade={trade}
                showReturn
                conversation={conversation}
                sourceId={returnSn}
              />
              {displaySensitive &&
                (platform === 'lazada' ? (
                  [
                    trade.totalAmount ? (
                      <TradePaymentAmountLazada
                        key="paymentAmountLazada"
                        trade={trade}
                        storeType={conversation?.topBarInfo?.storeType}
                        defaultFold={false}
                      />
                    ) : null,
                    trade.customTradeIncome?.length > 0 && (
                      <TradeDetailIncomeLazada
                        key="TradeDetailIncomeLazada"
                        trade={trade}
                        defaultFold={false}
                      />
                    ),
                  ]
                ) : !trade.payment ? null : platform === 'shopee' ? (
                  [
                    <TradePaymentAmountShopee
                      key="TradePaymentAmountShopee"
                      trade={trade}
                      defaultFold={false}
                    />,
                    <TradeDetailIncomeShopee
                      key="TradeDetailIncomeShopee"
                      trade={trade}
                      defaultFold={false}
                    />,
                  ]
                ) : isTiKTok(platform) ? (
                  <TradeDetailPaymentTikTok
                    key="paymentAmountTikTok"
                    trade={trade}
                    defaultFold={false}
                  />
                ) : platform === 'mercado' ? (
                  <TradeDetailIncomeMercado
                    key="TradeDetailIncomeMercado"
                    trade={trade}
                    defaultFold={false}
                  />
                ) : null)}
            </ListBody>
          </List>
          <CommentLine
            platform={toUpperCase(platform)}
            trade={trade}
            onUpdate={() => pullTrade(true)}
          />
        </section>
        <h3>{t('scene.orderWorkspace.returnRefundTab.detail.section.operationLog')}</h3>
        <section>
          {operationLog.length > 0 ? (
            <OperationLogs
              unit={unit}
              operationLog={operationLog}
              row={returnRow}
              externalTradeId={trade.externalTradeId}
            />
          ) : null}
        </section>
      </Content>
    </>
  );
}
